import { useState, useRef, useCallback } from 'react';
import { pipeline, env, type ProgressInfo } from '@huggingface/transformers';
import { useToast } from '@/hooks/use-toast';

// Configure transformers.js
env.allowLocalModels = false;
env.useBrowserCache = true;

type TranscriberPipeline = Awaited<ReturnType<typeof pipeline<'automatic-speech-recognition'>>>;

export const useSpeechToText = () => {
  const { toast } = useToast();
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [modelLoadProgress, setModelLoadProgress] = useState(0);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const transcriberRef = useRef<TranscriberPipeline | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const loadModel = useCallback(async () => {
    if (transcriberRef.current) return transcriberRef.current;
    
    setIsModelLoading(true);
    setModelLoadProgress(0);
    
    try {
      const transcriber = await pipeline(
        'automatic-speech-recognition',
        'onnx-community/whisper-small',
        {
          progress_callback: (progress: ProgressInfo) => {
            if ('progress' in progress && typeof progress.progress === 'number') {
              setModelLoadProgress(Math.round(progress.progress));
            }
          },
        }
      );
      
      transcriberRef.current = transcriber;
      setIsModelLoading(false);
      return transcriber;
    } catch (error) {
      console.error('Error loading Whisper model:', error);
      setIsModelLoading(false);
      toast({
        title: 'Fehler beim Laden',
        description: 'Das Spracherkennungsmodell konnte nicht geladen werden.',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  const startRecording = useCallback(async () => {
    try {
      // Pre-load model while requesting microphone
      const modelPromise = loadModel();
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });
      
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
          ? 'audio/webm;codecs=opus' 
          : 'audio/webm',
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      
      // Wait for model to be ready
      await modelPromise;
      
    } catch (error) {
      console.error('Microphone access error:', error);
      toast({
        title: 'Mikrofon-Zugriff verweigert',
        description: 'Bitte erlauben Sie den Zugriff auf das Mikrofon.',
        variant: 'destructive',
      });
    }
  }, [loadModel, toast]);

  const stopRecording = useCallback(async (): Promise<string> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || !isRecording) {
        resolve('');
        return;
      }
      
      mediaRecorderRef.current.onstop = async () => {
        // Stop all audio tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        
        if (audioChunksRef.current.length === 0) {
          resolve('');
          return;
        }
        
        setIsTranscribing(true);
        
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          
          // Convert blob to audio URL
          const audioUrl = URL.createObjectURL(audioBlob);
          
          // Use the transcriber
          if (transcriberRef.current) {
            const result = await transcriberRef.current(audioUrl, {
              language: 'german',
              task: 'transcribe',
            });
            
            URL.revokeObjectURL(audioUrl);
            
            const text = Array.isArray(result) 
              ? result.map(r => r.text).join(' ')
              : result.text;
            
            resolve(text?.trim() || '');
          } else {
            resolve('');
          }
        } catch (error) {
          console.error('Transcription error:', error);
          toast({
            title: 'Transkription fehlgeschlagen',
            description: 'Bitte versuchen Sie es erneut.',
            variant: 'destructive',
          });
          resolve('');
        } finally {
          setIsTranscribing(false);
        }
      };
      
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    });
  }, [isRecording, toast]);

  const toggleRecording = useCallback(async (): Promise<string> => {
    if (isRecording) {
      return stopRecording();
    } else {
      await startRecording();
      return '';
    }
  }, [isRecording, startRecording, stopRecording]);

  return {
    isRecording,
    isTranscribing,
    isModelLoading,
    modelLoadProgress,
    startRecording,
    stopRecording,
    toggleRecording,
  };
};
